import React, { createContext, useContext, useState, useCallback } from "react";
import { AlertTriangle, Info, ShieldAlert } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * ConfirmProvider — drop-in replacement for window.confirm / window.alert.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Delete workspace",
 *     description: "This permanently removes the workspace and all its assets.",
 *     confirmLabel: "Delete",
 *     destructive: true,
 *   });
 *   if (ok) { ... }
 */
const ConfirmCtx = createContext(null);

const VARIANTS = {
    danger:  { Icon: ShieldAlert,   iconClass: "text-primary" },
    warning: { Icon: AlertTriangle, iconClass: "text-primary" },
    info:    { Icon: Info,          iconClass: "text-muted-foreground" },
};

export function ConfirmProvider({ children }) {
    const [state, setState] = useState(null);

    const resolveRef = React.useRef(null);

    const confirm = useCallback((options) => {
        return new Promise((resolve) => {
            resolveRef.current = resolve;
            setState({
                title: "Are you sure?",
                description: "",
                confirmLabel: "Confirm",
                cancelLabel: "Cancel",
                variant: "warning",
                destructive: false,
                ...options,
            });
        });
    }, []);

    const close = (answer) => {
        setState(null);
        if (resolveRef.current) {
            resolveRef.current(answer);
            resolveRef.current = null;
        }
    };

    const v = VARIANTS[state?.destructive ? "danger" : (state?.variant || "warning")] || VARIANTS.warning;
    const Icon = v.Icon;

    return (
        <ConfirmCtx.Provider value={confirm}>
            {children}
            <Dialog open={!!state} onOpenChange={(o) => { if (!o) close(false); }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 shrink-0 rounded-sm border border-border bg-secondary/40 flex items-center justify-center ${v.iconClass}`}>
                                <Icon className="h-5 w-5" strokeWidth={1.75} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <DialogTitle data-testid="confirm-title">{state?.title}</DialogTitle>
                                {state?.description && (
                                    <DialogDescription className="mt-1.5 text-sm leading-relaxed" data-testid="confirm-description">
                                        {state.description}
                                    </DialogDescription>
                                )}
                            </div>
                        </div>
                    </DialogHeader>
                    <DialogFooter className="mt-4 gap-2">
                        {state?.cancelLabel !== null && (
                            <Button variant="outline" className="rounded-sm" onClick={() => close(false)} data-testid="confirm-cancel">
                                {state?.cancelLabel}
                            </Button>
                        )}
                        <Button
                            className={`rounded-sm ${state?.destructive ? "" : ""}`}
                            onClick={() => close(true)}
                            data-testid="confirm-ok"
                        >
                            {state?.confirmLabel}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </ConfirmCtx.Provider>
    );
}

export function useConfirm() {
    const fn = useContext(ConfirmCtx);
    if (!fn) throw new Error("useConfirm must be used within ConfirmProvider");
    return fn;
}
